import type { ManageAPI } from "~/manage-api/types";

const NDASH = '–';

export type DayOfTheWeek = { day: number; name: string }

const DTF = new Map<string, Intl.DateTimeFormat>();
const DAYS_OF_THE_WEEK = new Map<string, DayOfTheWeek[]>();

export function getDTF (locale: string, options: Intl.DateTimeFormatOptions) {
  const key = locale + '/' + Object.entries(options).map((e => e.join('='))).join(';')
  let dtf = DTF.get(key);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat(locale, options);
    DTF.set(key, dtf);
  }
  return dtf;
}

/**
 * 
 * @param day Day of the week as number (Sunday = 0)
 * @param locale 
 * @returns 
 */
export function getDayOfTheWeekName (day: number, locale: string) {

  const date = new Date();
  const prev_sunday = date.getDate() - date.getDay();
  date.setDate(prev_sunday + day);

  const result = getDTF(locale, { weekday: 'short' }).format(date);

  if (locale == 'nb-NO') return result.replace('.', ''); 
  return result;
}

/**
 * Returns human readable range of weekdays. E.g. "1,2,3" for English 
 * would return Mon–Wed.
 * @param days a comma separated list of weekday numbers e.g. "1,2,3" (Sun = 0)
 * @param locale 
 * @returns 
 */
export function toRangeOfDaysOfTheWeek (days: string, locale: string) {
  const [from, ...rest] = days.split(",").map(Number);
  const to = rest.pop();
  
  if (to == undefined || from == to) return getDayOfTheWeekName(from, locale);

  return getDayOfTheWeekName(from, locale) + NDASH + getDayOfTheWeekName(to, locale);
}

/**
 * Returns day of the week number for today.
 * @returns number (0-6)
 */
export function dayOfTheWeek () {
  return new Date().getDay();
}

export function getMonth () {
  return new Date().getMonth();
}

export function getDateOfTheMonth (date: string) {
  return new Date(date).getDate();
}

export function getMonthInLongFormat (date: string, locale: string) {
  const newDate = new Date(date);
  const monthName = new Intl.DateTimeFormat(locale, { month: "long"}).format;
  return monthName(newDate);
}

/**
 * Returns an array of localised day of the week names. 
 * @param locale 
 * @param weekday format style `long` | `short`
 * @returns 
 */
export function getDaysOfTheWeek(
    locale: string, 
    weekday: Intl.ListFormatStyle = 'long'
  ): readonly DayOfTheWeek[] {
  
  if (!locale) return [];

  const key = locale + '/' + weekday;
  let dotw = DAYS_OF_THE_WEEK.get(key);

  if (!dotw) {
    dotw = new Array(7);
    const dtf = getDTF(locale, { weekday })
    const date = new Date();
    date.setDate(date.getDate() - date.getDay()); // Move Sunday
    
    for (let index = 0; index < 7; index++) {
      date.setDate(date.getDate() + 1);
      dotw[index] = {
        day: (index + 1) % 7, // Sunday = 0
        name: dtf.format(date),
      };
    }
    DAYS_OF_THE_WEEK.set(key, dotw);
  }

  return dotw;
}

/**
 * Returns subset of localised day of the week names. 
 * @param locale language locale 
 * @param days to be included in the subset
 * @param weekday format style `long` | `short`
 * @returns 
 */
export function getSomeDaysOfTheWeek(
    locale: string,
    days: number[],
    weekday: Intl.ListFormatStyle = 'long'
  ): readonly DayOfTheWeek[] {

  const dotw = getDaysOfTheWeek(locale, weekday);
  return dotw.filter(x => days.includes(x.day));
}

export function getDayOfTheWeek(
  locale: string,
  day: number,
  weekday: Intl.ListFormatStyle = 'long'
): DayOfTheWeek {

  const dotw = getSomeDaysOfTheWeek(locale, [day], weekday);
  if (dotw.length) {
    return dotw[0];
  }
  return {
    name: `invalid day ${day}`,
    day
  };
}

export const nl2br = (text: string) => {
  return text && text.replace(/\n/g, '<br/>');
};

Date.prototype.getWeek = function () {
  const forthjan = new Date(this.getFullYear(), 0, 4);
  return Math.ceil(((this - forthjan) / 86400000 + forthjan.getDay() + 1) / 7);
};

export const weekNumber = new Date().getWeek();

/**
 * 
 * @param date_str date in Finnish style format i.e. 25.1.2024
 * @returns 
 */
export function parseDate_dd_MM_yy (date_str: string) {
  const [date, month, year] = date_str.split(".").map(Number);
  return new Date(year, month - 1, date);
}

/**
 * 
 * @param date_str date in ISO format i.e. 2024-01-25
 */
export function parseDateISO (date_str: string) {
  const [year, month, date] = date_str.split("-").map(Number);
  return new Date(year, month - 1, date);
}

export function parseDate_yyyy_mm_dd(selectedDate:Date = new Date()) {
  const day = selectedDate.getDay();
  return {day, date: formatISODate(selectedDate)} 
}

export const parseLunchMenuDate = parseDate_dd_MM_yy;


export function formatISODate (input: Date) {
  const year = input.getFullYear()
  const month = (input.getMonth() + 1).toString().padStart(2, "0");
  const date = input.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${date}`;
}

/**
 * 
 * @param date 
 * @param locale 
 * @param options 
 * @returns 
 */
export function formatDate (
  date: Date, 
  locale: string, 
  options: Intl.DateTimeFormatOptions,
) {
  const dtf = getDTF(locale, options)
  return dtf.format(date)
}

/**
 * Returns date string including just day and month in most short format.
 * @param date Date to format
 * @param locale Locale to format to
 * @returns 
 */
export function formatDate_short_dd_MM (date: Date, locale: string) {
  return formatDate(date, locale, { day: 'numeric', month: 'numeric' });
}

function time (h: number, m: number, is_next_day = false) {
  const date = new Date();
  date.setUTCHours(is_next_day ? h + 24 : h, m, 0, 0);
  return date;
}


export function parseTimeRange (locale: string, hfrom: number, mfrom: number, hto: number, mto: number, unforce_h23?: boolean) {
  
  const from = time(hfrom, mfrom);
  const to = time(hto, mto, hto <= hfrom);
  const optionsh23: Intl.DateTimeFormatOptions =  {  hour: '2-digit', minute: '2-digit', timeZone: 'UTC', hourCycle: 'h23' }
  const optionsNormal: Intl.DateTimeFormatOptions  =  {  hour: '2-digit', minute: '2-digit', timeZone: 'UTC' , hourCycle: 'h12'}

  // Forcing 24 hours clock for all locales w/ midnight as 00
  const dtf = getDTF(locale, unforce_h23 ? optionsNormal : optionsh23)
  const parts = dtf.formatRangeToParts(from, to);

  let is_time_parts = false;
  
  return parts.reduce((acc: string[], part) => {
    const { source, type, value } = part;

    if (type == 'hour') {
      is_time_parts = true;
      if (source == 'endRange') acc.push(value.padStart(2, '0'));
      else acc.push(value == '0' ? '00' : value);
    }
    else if (is_time_parts) {
      // Drop minutes
      if (type == 'minute' && value == '00') {
        acc.pop(); // Remove separator
      }
      else {
        if (source == 'shared') {
          is_time_parts = false;
        }
        acc.push(value);
      }
    }

    return acc;
  }, new Array(12)).join('');
}

function splitTime (time: string): [number, number] {
  const [h, m] = time.split(':').map(x => parseInt(x, 10));
  return [h, m];
}

export function parseOpeningHoursItem (locale: string, item: ManageAPI.OpeningHoursItem, unforce_h23?:boolean): string[] {
  return item.dailyOpeningHours?.map(hours => 
    parseTimeRange(locale, ...splitTime(hours.from), ...splitTime(hours.to), unforce_h23)
  ) || [];
}

export function parseExceptionalOpeningHoursItem (locale: string, item: ManageAPI.ExceptionalOpeningHoursItem, unforce_h23?:boolean): string {
  const { fromHour, fromMinute, toHour, toMinute } = item;
  return parseTimeRange(locale, fromHour, fromMinute, toHour, toMinute , unforce_h23);
}

const CLOSED: ManageAPI.OpeningHoursItem = {
  dailyOpeningHours: [],
  days: '0,1,2,3,4,5,6',
  openingStatus: 'CLOSED',
}


export function findOpeningHoursByDate(
  opening_hour_items: ManageAPI.OpeningHoursItem[], 
  exceptional_hour_items: ManageAPI.ExceptionalOpeningHoursItem[], 
  selectedDate?: Date
): ManageAPI.OpeningHoursItem | ManageAPI.ExceptionalOpeningHoursItem | undefined {

  const {date, day} = parseDate_yyyy_mm_dd(selectedDate);

  const eoh = exceptional_hour_items?.find(oh => oh.date == date)
  if (eoh) return eoh

  const day_str = String(day);
  const oh = opening_hour_items?.find(oh => oh.days.includes(day_str))
  if (oh) return oh

  return undefined;
};

export function todaysOpeningHoursItem (
  store: Pick<ManageAPI.Store, 'exceptionalOpeningHours' | 'unformattedOpeningHours'>
): ManageAPI.OpeningHoursItem | ManageAPI.ExceptionalOpeningHoursItem {

  return findOpeningHoursByDate(
    store.unformattedOpeningHours, 
    store.exceptionalOpeningHours,
  ) || CLOSED;
}

export function findVenueOpeningHoursByDate(openingHours: ManageAPI.OpeningHours, selectedDate?: Date) {

  return findOpeningHoursByDate(
    openingHours.shoppingCenterOpeningHours, 
    openingHours.exceptionalOpeningHours,
    selectedDate,
  ) || CLOSED;
};

export function getOpeningHoursRangesAndStatusByDate( opening_hours: ManageAPI.OpeningHoursItem[], exceptionalOpeningHours: ManageAPI.ExceptionalOpeningHoursItem[], date = new Date(), locale: string) {
  let ranges: string[] = [];
  let status: string = "";
  const item = findOpeningHoursByDate(opening_hours, exceptionalOpeningHours, date);

  if (item && "days" in item) {
      status = item.openingStatus;
      ranges = parseOpeningHoursItem(locale, item);
  }

  if (item && "reason" in item) {
      status = item.open ? "OPEN" : "CLOSED";
      ranges = item.open
          ? [parseExceptionalOpeningHoursItem(locale, item)]
          : [];
  }
  return { ranges, status }
}

/**
 * @deprecated This function doesn't provide locale formating. Use instead `parseDateISO` + `formatDate_short_dd_MM` or `formatDate`.
 */
export function parseDate(params: string) {
  const [year, month, date] = params.split("-");
  return (date + "." + month + '.' + year)

}

/**
 * @deprecated This function doesn't provide locale formating. Use instead `parseExceptionalOpeningHoursItem`.
 */
export function parseExceptionalOpeningHoursTimeRange(day: ManageAPI.ExceptionalOpeningHoursItem ): string {
  let { fromHour, fromMinute, toHour, toMinute } = day
  return `${fromHour}${fromMinute == 0 ? "" : ":" + fromMinute}${NDASH}${toHour}${toMinute ==
      0
      ? ""
      : ":" + toMinute}`
}
